import React, { FC, useCallback } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { FavoriteIcon } from 'components/favorite-icon';
import Animation from 'components/lottie-animations/favorite-loading/favorite-loading';
import Picture from 'components/picture';
import { useFavoriteOffers } from 'hooks/favorite-offers';
import { useAuthContext } from 'state/auth';
import { routes } from 'utils/routing';

import {
  CarouselItem,
  FavoriteIconButton,
  LoadingWrapper,
  OfferDescription,
  OfferDetails,
  OfferTitle,
  PictureWrapper,
} from './offer-carousel-section.styled';
import { OfferItemProps } from './types';

const OfferCarouselItem: FC<OfferItemProps> = ({
  offerId,
  favoriteId,
  offerName,
  description,
  image,
  imageAlt,
  fullWidth,
  onClick,
}) => {
  const { formatMessage } = useIntl();
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const { toggleFavorite, isFavorite, isOfferLoading } = useFavoriteOffers();
  const favorited = isFavorite(favoriteId || '');

  const handleFavoriteClick = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();

      if (!isAuthenticated()) {
        return navigate(routes.signUp);
      }

      if (favoriteId) {
        toggleFavorite(favoriteId, offerName || '');
      }
    },
    [favoriteId, isAuthenticated, navigate, offerName, toggleFavorite]
  );

  return (
    <CarouselItem key={offerId} onClick={onClick} fullWidth={fullWidth}>
      <FavoriteIconButton
        data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
        onClick={handleFavoriteClick}
        aria-label={formatMessage({
          id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
        })}
        isTransparent={true}
      >
        {isOfferLoading(favoriteId || '') ? (
          <LoadingWrapper>
            <Animation />
          </LoadingWrapper>
        ) : (
          <FavoriteIcon isFavorite={favorited} />
        )}
      </FavoriteIconButton>
      <PictureWrapper>
        <Picture image={image} alt={imageAlt || ''} objectFitContain={false} />
      </PictureWrapper>
      <OfferDetails>
        <OfferTitle>{offerName}</OfferTitle>
        <OfferDescription>{description}</OfferDescription>
      </OfferDetails>
    </CarouselItem>
  );
};

export default OfferCarouselItem;
